// =========================
// DK Cart Drawer (stable v3 - smooth qty like cart.js)
// - Delegated toggle click (voorkomt /cart navigatie)
// - Qty: optimistic UI + debounced commit + ONE refresh after idle
// - Blocks other theme handlers (pointerdown capture)
// - AJAX remove/upsell
// - Refresh zonder auto-open
// =========================

(async function DKCartDrawerInit() {
  // -------------------------
  // Helpers
  // -------------------------
  async function DK_updateCartCountFromCart(cart) {
    try {
      if (!cart) return;
      document.querySelectorAll('.cart-count').forEach(el => {
        el.textContent = cart.item_count;
      });
    } catch (e) {}
  }

  async function DK_fetchCart() {
    const root =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
        ? window.Shopify.routes.root
        : '/';
    const res = await fetch(`${root}cart.js`, { headers: { 'Accept': 'application/json' } });
    if (!res.ok) return null;
    return await res.json();
  }

  const clamp = (val, min, max) => {
    let v = val;
    if (!Number.isFinite(v)) v = min;
    if (v < min) v = min;
    if (max !== null && v > max) v = max;
    return v;
  };

  const getMinMax = (input) => {
    // default min=1 zodat min-knop niet “verwijderen” wordt
    const min = input.min ? parseInt(input.min, 10) : 1;
    const max = input.max ? parseInt(input.max, 10) : null;
    return { min, max };
  };

  // -------------------------
  // CartDrawer Web Component
  // -------------------------
  class CartDrawer extends HTMLElement {
    constructor() {
      super();

      this.cartDrawer = this;
      this.cartDrawerClose = this.querySelector('.cart-drawer-close');
      this.cartDrawerOverlay = this.querySelector('.cart-drawer-overlay');
      this.cartDrawerEmptyRedirect = this.querySelector('.cart-drawer-empty-footer');

      this.setAttribute('tabindex', '-1');
      this.previousElement = null;

      // one refresh timer for the whole drawer (prevents “flicker”)
      this._dkRefreshTimer = null;

      // Close handlers
      if (this.cartDrawerClose) {
        this.cartDrawerClose.addEventListener('click', () => this.close());
        this.cartDrawerClose.addEventListener('keyup', (event) => {
          if (event.key === 'Enter') this.close();
        });
      }

      if (this.cartDrawerOverlay) {
        this.cartDrawerOverlay.addEventListener('click', () => this.close());
      }

      document.addEventListener('keyup', (event) => {
        if (event.key === 'Escape') this.close();
      });

      if (this.cartDrawerEmptyRedirect) {
        this.cartDrawerEmptyRedirect.addEventListener('click', () => this.close());
      }

      this.bindLineEvents();
    }

    open(opts = {}) {
      const { focus = true } = opts;

      this.previousElement = (document.activeElement || this.previousElement);

      this.cartDrawer.setAttribute('aria-hidden', 'false');
      this.cartDrawer.classList.add('active');
      document.body.classList.add('dk-cart-drawer-open');

      if (focus) {
        setTimeout(() => {
          const first = this.querySelector(
            'a[href], button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
          );
          if (first) first.focus();
        }, 60);
      }
    }

    close() {
      try {
        if (this.previousElement && typeof this.previousElement.focus === 'function') {
          this.previousElement.focus();
        }
      } catch (e) {}

      this.cartDrawer.setAttribute('aria-hidden', 'true');
      this.cartDrawer.classList.remove('active');
      document.body.classList.remove('dk-cart-drawer-open');
    }

    // Loopt er nog een aantal-wijziging (wachten op commit of request onderweg)?
    isQtyBusy() {
      return Array.from(this.querySelectorAll('product-quantity-picker')).some(
        (p) => p._dkTimer || p.dataset.dkBusy === '1'
      );
    }

    queueRefresh(delay = 260) {
      // na replaceWith() kan 'this' losgekoppeld zijn → altijd de live drawer
      const live = this.isConnected ? this : document.querySelector('cart-drawer.cart-drawer');
      if (!live) return;
      if (live._dkRefreshTimer) clearTimeout(live._dkRefreshTimer);
      live._dkRefreshTimer = setTimeout(() => {
        live.refresh({ open: false });
      }, delay);
    }

    async refresh(options = {}) {
      const { open = false } = options;

      if (!this.isConnected) {
        const live = document.querySelector('cart-drawer.cart-drawer');
        if (live && live !== this) return live.refresh(options);
        return;
      }

      // Niet vervangen terwijl de klant nog aan het klikken is:
      // anders springt het aantal terug en gaan klikken verloren.
      if (this.isQtyBusy()) {
        this.queueRefresh(250);
        return;
      }
      const interactionAtStart = this._dkInteraction || 0;

      // section render van huidige page
      const url = `${window.location.pathname}?section_id=cart-drawer`;

      window.__DK_DRAWER_INTERNAL__ = true;

      try {
        const res = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!res.ok) return;

        const html = await res.text();
        const tmp = document.createElement('div');
        tmp.innerHTML = html;

        const newDrawer = tmp.querySelector('cart-drawer.cart-drawer');
        if (!newDrawer) return;

        // Tijdens het ophalen opnieuw geklikt? Deze HTML is verouderd → later opnieuw.
        if ((this._dkInteraction || 0) !== interactionAtStart || this.isQtyBusy()) {
          this.queueRefresh(250);
          return;
        }

        const wasOpen = this.classList.contains('active');
        const oldBody = this.querySelector('.cart-drawer-body');
        const scrollTop = oldBody ? oldBody.scrollTop : 0;

        // Replace
        this.replaceWith(newDrawer);

        // Scrollpositie behouden (niet terug naar boven springen)
        const newBody = newDrawer.querySelector('.cart-drawer-body');
        if (newBody && scrollTop) newBody.scrollTop = scrollTop;

        // Upgrade/init
        try { customElements.upgrade(newDrawer); } catch (e) {}

        // Open state behouden tenzij expliciet open:true
        if (wasOpen || open) {
          newDrawer.classList.add('active');
          newDrawer.setAttribute('aria-hidden', 'false');
          document.body.classList.add('dk-cart-drawer-open');
        } else {
          newDrawer.classList.remove('active');
          newDrawer.setAttribute('aria-hidden', 'true');
          document.body.classList.remove('dk-cart-drawer-open');
        }

        // after render, sync count from real cart (absolute, not incremental)
        const cart = await DK_fetchCart();
        if (cart) await DK_updateCartCountFromCart(cart);
      } finally {
        window.__DK_DRAWER_INTERNAL__ = false;
      }
    }

    async changeLineByKey(key, quantity) {
      window.__DK_DRAWER_INTERNAL__ = true;
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: key, quantity })
        });
        if (!res.ok) return null;
        // Shopify change.js returns the full cart JSON
        return await res.json();
      } finally {
        window.__DK_DRAWER_INTERNAL__ = false;
      }
    }

    async addVariant(variantId, quantity) {
      window.__DK_DRAWER_INTERNAL__ = true;
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: Number(variantId), quantity: Number(quantity) })
        });
        if (!res.ok) return null;
        return await res.json(); // add.js returns added item, not full cart
      } finally {
        window.__DK_DRAWER_INTERNAL__ = false;
      }
    }

    bindLineEvents() {
      // ==========================================================
      // 0) Kill other theme handlers as early as possible (capture)
      // ==========================================================
      const blockIfQtyOrRemove = (e) => {
        const t = e.target;
        if (!t) return;

        const isQtyBtn = t.closest('.qty-minus, .qty-plus');
        const isRemove = t.closest('a.cart-product-remove');
        if (!isQtyBtn && !isRemove) return;

        // allow ctrl/cmd/middle click defaults where relevant
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;

        e.preventDefault();
        e.stopPropagation();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();
      };

      // capture phase: vóór andere scripts
      this.addEventListener('pointerdown', blockIfQtyOrRemove, true);

      // ==========================================================
      // 1) Smooth qty engine
      // - UI direct mee
      // - commit pas na idle
      // - 1 request tegelijk + follow-up
      // - refresh pas na idle (niet na elke klik!)
      // ==========================================================
      const scheduleCommit = (picker, input) => {
        if (picker._dkTimer) clearTimeout(picker._dkTimer);

        picker._dkTimer = setTimeout(async () => {
          picker._dkTimer = null; // deze timer is afgegaan (nieuwe klikken zetten een nieuwe)
          const { min, max } = getMinMax(input);

          const rawTarget = (picker.dataset.dkTarget != null && picker.dataset.dkTarget !== '')
            ? parseInt(picker.dataset.dkTarget, 10)
            : parseInt(input.value, 10);

          const target = clamp(rawTarget, min, max);

          // Zet UI netjes (voorkomt rare/lege waarden)
          input.value = String(target);
          picker.dataset.dkTarget = String(target);

          // als er al request loopt → follow-up
          if (picker.dataset.dkBusy === '1') {
            picker._dkNeedsFollowUp = '1';
            return;
          }

          picker.dataset.dkBusy = '1';

          try {
            const cart = await this.changeLineByKey(input.name, target);
            if (!cart) return;

            // update count direct (absolute)
            await DK_updateCartCountFromCart(cart);

            // refresh pas NA idle / coalesced (dit voorkomt “getallen flikkeren”)
            this.queueRefresh(220);
          } finally {
            picker.dataset.dkBusy = '0';

            if (picker._dkNeedsFollowUp === '1') {
              picker._dkNeedsFollowUp = '0';
              scheduleCommit(picker, input);
            }
          }
        }, 350); // wacht tot de klant klaar is met klikken, dan 1 request
      };

      // +/- click (ONE handler)
      this.addEventListener('click', (e) => {
        const minus = e.target.closest('.qty-minus');
        const plus  = e.target.closest('.qty-plus');
        if (!minus && !plus) return;

        // (pointerdown capture blokkeert al, maar voor zekerheid)
        e.preventDefault();
        e.stopPropagation();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();

        const picker = e.target.closest('product-quantity-picker');
        if (!picker) return;

        const input = picker.querySelector('input.product-quantity-picker-input');
        if (!input || input.disabled) return;

        const { min, max } = getMinMax(input);

        const base = (picker.dataset.dkTarget != null && picker.dataset.dkTarget !== '')
          ? parseInt(picker.dataset.dkTarget, 10)
          : parseInt(input.value, 10);

        const current = clamp(base, min, max);
        const next = clamp(current + (plus ? 1 : -1), min, max);

        this._dkInteraction = (this._dkInteraction || 0) + 1;

        // optimistic UI
        input.value = String(next);
        picker.dataset.dkTarget = String(next);

        scheduleCommit(picker, input);
      });

      // typing in input (optional, maar netjes)
      this.addEventListener('input', (e) => {
        const input = e.target.closest('input.product-quantity-picker-input');
        if (!input || input.disabled) return;

        const picker = input.closest('product-quantity-picker');
        if (!picker) return;

        // alleen digits (voorkomt “rommel”)
        const cleaned = String(input.value).replace(/[^\d]/g, '');
        input.value = cleaned;
        picker.dataset.dkTarget = cleaned;

        scheduleCommit(picker, input);
      });

      this.addEventListener('change', (e) => {
        const input = e.target.closest('input.product-quantity-picker-input');
        if (!input || input.disabled) return;

        const picker = input.closest('product-quantity-picker');
        if (!picker) return;

        const { min, max } = getMinMax(input);
        const raw = parseInt(input.value, 10);
        const next = clamp(Number.isFinite(raw) ? raw : min, min, max);

        input.value = String(next);
        picker.dataset.dkTarget = String(next);

        scheduleCommit(picker, input);
      });

      // ==========================================================
      // 2) Remove (AJAX)
      // ==========================================================
      this.addEventListener('click', async (e) => {
        const removeLink = e.target.closest('a.cart-product-remove');
        if (!removeLink) return;

        e.preventDefault();
        e.stopPropagation();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();

        const li = removeLink.closest('li[data-item-key]');
        if (!li) return;

        const key = li.getAttribute('data-item-key');
        const cart = await this.changeLineByKey(key, 0);
        if (cart) {
          await DK_updateCartCountFromCart(cart);
          this.queueRefresh(120);
        }
      });

      // ==========================================================
      // 3) Upsell (AJAX)
      // ==========================================================
      this.addEventListener('change', async (e) => {
        const checkbox = e.target.closest('#cartDrawerGiftWrap');
        if (!checkbox) return;

        const wrap = this.querySelector('.cart-upsell');
        if (!wrap) return;

        const variantId = wrap.getAttribute('data-item-id');
        const qty = parseInt(wrap.getAttribute('data-item-qty') || '1', 10);

        if (checkbox.checked) {
          const added = await this.addVariant(variantId, Math.max(qty, 1));
          if (added) this.queueRefresh(120);
        } else {
          const res = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: Number(variantId), quantity: 0 })
          });
          if (res.ok) this.queueRefresh(120);
        }
      });
    }
  }

  // Define once
  if (!customElements.get('cart-drawer')) {
    customElements.define('cart-drawer', CartDrawer);
  }

  // -------------------------
  // Delegated toggle open
  // (fix voor /cart navigatie)
  // -------------------------
  (function DK_cartDrawerToggleDelegation() {
    if (window.__DK_CARTDRAWER_TOGGLE__) return;
    window.__DK_CARTDRAWER_TOGGLE__ = true;

    const selectors = [
      '.section-header-cart-drawer-toggle',
      '[data-cart-drawer-toggle]',
      'a[href="/cart"]',
      'a[href$="/cart"]',
      'a[href*="/cart?"]'
    ].join(',');

    const handler = (e) => {
      const target = e.target;
      if (!target) return;

      const clicked = target.closest(selectors);
      if (!clicked) return;

      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;

      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();

      const drawer = document.querySelector('cart-drawer.cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open({ focus: true });
      }
    };

    document.addEventListener('click', handler, true);
    document.addEventListener('pointerdown', handler, true);
  })();

  // -------------------------
  // Auto refresh after cart mutations (no auto-open)
  // -------------------------
  (function DK_cartDrawerAutoRefresh() {
    if (window.__DK_CARTDRAWER_HOOK__) return;
    window.__DK_CARTDRAWER_HOOK__ = true;

    const shouldWatch = (url) => {
      if (!url) return false;
      return (
        url.includes('/cart/add') ||
        url.includes('/cart/change') ||
        url.includes('/cart/update') ||
        url.includes('/cart/clear')
      );
    };

    // /cart/update.js met alleen attributes/note (bv. voorwaarden-app of
    // cart-note) verandert niets aan de items → NIET verversen. Anders wordt
    // de drawer vervangen en springt het voorwaarden-vinkje van de app terug.
    const isAttributesOnlyUpdate = (url, init) => {
      if (!url.includes('/cart/update')) return false;
      const body = init && init.body;
      if (!body) return false;
      try {
        if (typeof body === 'string') return !/updates/.test(body);
        if (typeof body.keys === 'function') {
          for (const key of body.keys()) {
            if (String(key).indexOf('updates') === 0) return false;
          }
          return true;
        }
      } catch (e) {}
      return false;
    };

    const refreshOnly = async () => {
      const drawer = document.querySelector('cart-drawer.cart-drawer');
      if (!drawer || typeof drawer.refresh !== 'function') return;
      await drawer.refresh({ open: false });
    };

    const origFetch = window.fetch;
    window.fetch = async function(input, init) {
      const url = typeof input === 'string'
        ? input
        : (input && input.url) ? input.url : '';

      const res = await origFetch.apply(this, arguments);

      try {
        if (window.__DK_DRAWER_INTERNAL__) return res;

        if (res && res.ok && shouldWatch(url) && !isAttributesOnlyUpdate(url, init)) {
          setTimeout(refreshOnly, 80);
        }
      } catch (e) {}

      return res;
    };

    setTimeout(refreshOnly, 120);

    document.addEventListener('cart:updated', () => refreshOnly());
    document.addEventListener('cart:refresh', () => refreshOnly());
  })();

  // init count once
  try {
    const cart = await DK_fetchCart();
    if (cart) await DK_updateCartCountFromCart(cart);
  } catch (e) {}
})();
